<template>
  <v-alert v-if="!model?.id || model.id === '0'" class="my-2" type="info"
    >Users can be added to group after it has been created.</v-alert
  >
  <template v-else>
    <v-card>
      <v-card-title>Selected Users</v-card-title>
      <v-card-text>
        <BaseListV2
          ref="memberList"
          :filter-prefix="'member'"
          :loading="loading"
          :can-add="false"
          :can-delete="false"
          :custom-actions="[removeMembershipAction]"
          :has-detail-view="false"
          :collection-filter="groupMemberFilter"
          :model-class="GroupMembership"
        >
          <template #filters>
            <v-text-field
              v-model="groupMemberSearch"
              max-width="300"
              label="Username"
              placeholder="Search by username..."
              icon="magnify"
              @blur="filterGroupMembers"
              @keyup.enter="filterGroupMembers"
            />
          </template>
        </BaseListV2>
      </v-card-text>
    </v-card>
    <v-card>
      <v-card-title>Available Users</v-card-title>
      <v-card-text>
        <BaseListV2
          ref="profileList"
          :filter-prefix="'profile'"
          :loading="props.form.loading"
          :can-add="false"
          :has-detail-view="false"
          :custom-actions="[addProfileAction]"
          :collection-filter="profileFilter"
          :model-class="Profile"
          :pre-selected-rows="members"
        >
          <template #filters>
            <v-container>
              <v-row class="ga-2 flex align-start">
                <v-sheet>
                  <p>Organisation</p>
                  <OrganisationSelector @selected="organisationSelected">
                    <template #modal-visibility-btn-content>
                      {{ selectedOrgLabel }}
                    </template>
                  </OrganisationSelector>
                </v-sheet>

                <v-select
                  v-model="kind"
                  max-width="200"
                  label="User kind"
                  placeholder="Any"
                  :items="[
                    { title: 'Any', value: null },
                    { title: 'Human', value: 'human' },
                    { title: 'Machine', value: 'machine' },
                  ]"
                  @update:model-value="filterUsers"
                />
                <v-text-field
                  v-model="usernameSearch"
                  label="Username"
                  max-width="300"
                  placeholder="Search by username..."
                  icon="magnify"
                  @blur="filterUsers"
                  @keyup.enter="filterUsers"
                />
              </v-row>
            </v-container>
          </template>
        </BaseListV2>
      </v-card-text>
    </v-card>
  </template>
</template>

<script setup lang="ts">
import { computed, Ref, ref, watch } from 'vue'
import { onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import { useSnackbar } from 'vuetify-use-dialog'
import { useStore } from 'vuex'

import { CollectionFilter } from '@/api/ApiClient'
import { apiClientV2, Context } from '@/api/ApiClientV2'
import BaseListV2 from '@/components/common/BaseListV2.vue'
import { SchemaFormKitProps } from '@/components/common/forms/editor-fields/formkit/types'
import OrganisationSelector from '@/components/navigation/sidebar/OrganisationSelector.vue'
import { GroupMembership } from '@/models/core/models'
import { Organisation } from '@/models/core/organisation'
import { Profile } from '@/models/core/profile'

const createSnackbar = useSnackbar()
const loading = ref(false)
const store = useStore()
const route = useRoute()

const model = defineModel<{
  id: string
}>()
const props = defineProps<SchemaFormKitProps>()
const kind = ref('human')
const selectedOrg = ref<string>(
  route.query.organisation
    ? route.query.organisation
    : store.getters['global/organisation'].id
)
const profileFilter: Ref<CollectionFilter> = ref(null)
const groupMemberFilter: Ref<CollectionFilter> = ref(null)
const usernameSearch: Ref<string> = ref(null)
const groupMemberSearch: Ref<string> = ref(null)
const addProfileAction = ref({
  label: 'Add User',
  type: 'is-info',
  icon: 'mdi-account-check',
  callback: null,
})
const removeMembershipAction = ref({
  label: 'Remove',
  type: 'is-danger',
  icon: 'mdi-account-remove',
  callback: null,
})
const profileList: Ref<typeof BaseListV2> = ref(null)
const memberList: Ref<typeof BaseListV2> = ref(null)
const members: Ref<string[]> = ref([])
const context = computed(() => store.state.global.context)
const selectedOrgLabel = computed(
  () =>
    context.value.organisations.objects.find((o) => o.id === selectedOrg.value)
      ?.name
)
watch(model, () => {
  init()
})

onBeforeMount(() => {
  init()
})

function init() {
  filterGroupMembers()
  filterUsers()
  addProfileAction.value.callback = addProfile
  removeMembershipAction.value.callback = removeMembership
  getGroupMembers()
}

function organisationSelected(newOrg: Organisation): void {
  if (!newOrg.id) return
  selectedOrg.value = newOrg.id
  filterUsers()
}

function addProfile(profile: Profile) {
  loading.value = true
  apiClientV2
    .create(GroupMembership, {
      group: model.value?.id,
      profile: profile.id,
    })
    .then(() => {
      createSnackbar({
        text: 'Successfully added!',
        snackbarProps: { color: 'green-darken-3' },
      })
      loading.value = false
      profileList.value?.clearErrors()
      memberList.value?.refreshCollection()
      getGroupMembers()
    })
    .catch((error) => {
      loading.value = false
      profileList.value?.handleError(error)
    })
}

function removeMembership(membership: GroupMembership) {
  loading.value = true
  apiClientV2
    .delete(GroupMembership, membership.id)
    .then(() => {
      createSnackbar({
        text: 'Successfully removed!',
        snackbarProps: { color: 'green-darken-3' },
      })
      loading.value = false
      memberList.value?.refreshCollection()
      getGroupMembers()
    })
    .catch((error) => {
      // TODO: Use form.$valid for this!!
      loading.value = false
      memberList.value?.handleError(error)
    })
}

function filterGroupMembers() {
  groupMemberFilter.value = {
    group: model.value?.id,
    search: groupMemberSearch.value,
  }
}

function filterUsers() {
  profileFilter.value = {
    organisation: selectedOrg.value,
    kind: kind.value,
    search: usernameSearch.value,
  }
}

async function getGroupMembers() {
  // only get group memebers when group has been created
  if (model.value?.id !== '0') {
    try {
      const context: Context = {
        filter: {
          group: model.value.id,
        },
        pagination: {
          page: 1,
          pageSize: 500,
        },
      }
      const results = await apiClientV2.getListItems<GroupMembership>(
        GroupMembership,
        context
      )
      members.value = results.map((object) => object.profile)
    } catch (err) {
      profileList.value?.handleError(err)
    }
  }
}
</script>
<style lang="scss">
.profiles-select tr.selected-row {
  background-color: #dbdbdb;
  color: #7a7a7a;
  opacity: 0.5;
}
</style>
